import { weekFileName } from '../../lib/weekFileName';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  FileText, AlertTriangle, ClipboardCheck, GraduationCap, 
  Calendar, AlertCircle, Clock, TrendingUp, BarChart3, PieChart,
  ArrowRight, RefreshCcw, Download, Archive, Bell, Award, User, Mail
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { DateText } from '../ui/week-badge';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Color schemes
const COLORS = {
  severity: {
    Critical: '#ef4444',
    Major: '#f97316',
    Minor: '#eab308',
  },
  priority: {
    Critical: '#ef4444',
    High: '#f97316',
    Medium: '#eab308',
    Low: '#22c55e',
  },
  status: {
    Open: '#eab308',
    Investigating: '#3b82f6',
    'Action Required': '#f97316',
    Verification: '#8b5cf6',
    Closed: '#22c55e',
    Planning: '#8b5cf6',
    Implementation: '#6366f1',
    Planned: '#3b82f6',
    'In Progress': '#eab308',
    Completed: '#22c55e',
    Cancelled: '#6b7280',
    Draft: '#6b7280',
    'Pending Review': '#eab308',
    'Pending Approval': '#3b82f6',
    Approved: '#22c55e',
    Obsolete: '#ef4444',
  },
  chart: ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6', '#f97316', '#06b6d4', '#ec4899'],
};

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
        <p className="font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onClick}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`shrink-0 p-2.5 sm:p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100').replace('-500', '-100')}`}>
          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Pie Chart Card
const PieChartCard = ({ title, data, colors }) => {
  const { t } = useTranslation();
  // Translate legend labels while keeping the original English key
  // for the colour-map lookup (backend returns English category names).
  const translated = (data || []).map((d) => ({
    ...d,
    _colorKey: d.name,
    name: t(`iso9001.chartLegend.${d.name}`, d.name),
  }));
  const total = translated.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {total > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <RechartsPieChart>
              <Pie
                data={translated}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {translated.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={colors[entry._colorKey] || COLORS.chart[index % COLORS.chart.length]} 
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-gray-400">
            {t('iso9001.chartLegend.noData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Bar Chart Card
const BarChartCard = ({ title, data, dataKey = 'value', color = '#3b82f6' }) => {
  const { t } = useTranslation();
  const translated = (data || []).map((d) => ({
    ...d,
    name: t(`iso9001.chartLegend.${d.name}`, d.name),
  }));
  const hasData = translated.some(item => item[dataKey] > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={translated}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey={dataKey} 
                fill={color}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-gray-400">
            {t('iso9001.chartLegend.noData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Line/Area Chart Card
const TrendChartCard = ({ title, data, dataKey = 'value', color = '#3b82f6' }) => {
  const { t } = useTranslation();
  const hasData = data.length > 0 && data.some(item => item[dataKey] > 0);
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey={dataKey} 
                stroke={color}
                fill={color}
                fillOpacity={0.2}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-gray-400">
            {t('iso9001.chartLegend.noData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Timeline Chart Card
const TimelineChartCard = ({ title, data }) => {
  const { t } = useTranslation();
  // Backend produces period strings like "Days 0-30", "30-60 days" or
  // "Overdue/Due Now". Parse them into i18n keys so the Y axis reads
  // the viewer's language without changing the API contract.
  const translatePeriod = (raw) => {
    if (!raw) return raw;
    const s = String(raw).trim();
    if (/^overdue/i.test(s) || /due now/i.test(s)) {
      return t('iso9001.timelinePeriod.overdueDueNow');
    }
    // "Days 0-30" / "Days 30-60" / …
    let m = s.match(/^days\s+(\d+)\s*[-–]\s*(\d+)/i);
    if (m) return t('iso9001.timelinePeriod.daysRange', { from: m[1], to: m[2] });
    // "0-30 days" / "30-60 days" / …
    m = s.match(/^(\d+)\s*[-–]\s*(\d+)\s+days?$/i);
    if (m) return t('iso9001.timelinePeriod.daysRange', { from: m[1], to: m[2] });
    return s;  // Fallback: unrecognised patterns pass through unchanged.
  };
  const translated = (data || []).map((d) => ({ ...d, period: translatePeriod(d.period) }));
  const hasData = translated.length > 0 && translated.some(item => item.count > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={translated} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="period" tick={{ fontSize: 10 }} width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[150px] flex items-center justify-center text-gray-400">
            {t('iso9001.chartLegend.noItemsScheduled')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Recent Item Component
const RecentItem = ({ number, title, status, severity, date }) => {
  const getStatusColor = (status) => COLORS.status[status] || '#6b7280';
  const getSeverityColor = (severity) => {
    const colors = {
      critical: '#ef4444',
      major: '#f97316',
      minor: '#eab308',
      high: '#ef4444',
      medium: '#f97316',
      low: '#22c55e',
    };
    return colors[severity] || '#6b7280';
  };

  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{number}</p>
        <p className="text-xs text-gray-500 truncate">{title}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {severity && (
          <Badge 
            style={{ backgroundColor: `${getSeverityColor(severity)}20`, color: getSeverityColor(severity) }}
          >
            {severity}
          </Badge>
        )}
        <Badge 
          style={{ backgroundColor: `${getStatusColor(status)}20`, color: getStatusColor(status) }}
        >
          {status}
        </Badge>
      </div>
    </div>
  );
};

// Main Dashboard Component
const ISODashboardCharts = ({ dashboardData, token, onNavigate }) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState(null);
  const [isLoadingCharts, setIsLoadingCharts] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [isExporting, setIsExporting] = useState(false);
  const [expiringCerts, setExpiringCerts] = useState(null);
  const [isLoadingCerts, setIsLoadingCerts] = useState(true);
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  const fetchChartData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/dashboard/charts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setChartData(data);
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    } finally {
      setIsLoadingCharts(false);
    }
  }, [token]);

  const fetchExpiringCertifications = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/expiring-certifications?days=90`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setExpiringCerts(data);
      }
    } catch (error) {
      console.error('Failed to fetch expiring certifications:', error);
    } finally {
      setIsLoadingCerts(false);
    }
  }, [token]);

  const handleSendAllNotifications = async () => {
    setIsSendingNotification(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/check-certifications`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Failed to send notifications');
      }
      
      toast.success(t('auto.iso.notificationsSentToAllAffectedEmployees'));
      fetchExpiringCertifications();
    } catch (error) {
      console.error('Failed to send notifications:', error);
      toast.error(t('auto.iso.failedToSendNotificationsCheckSmtp'));
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handleBatchExport = async () => {
    setIsExporting(true);
    toast.info(t('auto.iso.generatingComplianceExportThisMayTake'));
    
    try {
      const response = await fetch(`${API_BASE}/api/iso/batch-export/zip`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate export');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`ISO9001_Compliance_Export_${format(new Date(), 'yyyy-MM-dd')}.zip`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('auto.iso.complianceExportDownloadedSuccessfully'));
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('auto.iso.failedToGenerateComplianceExport'));
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchChartData();
      fetchExpiringCertifications();
    }
  }, [fetchChartData, fetchExpiringCertifications, token]);

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('auto.iso.loadingDashboard')}</div>
      </div>
    );
  }

  const { documents, ncrs, capas, audits, training } = dashboardData;

  return (
    <div className="space-y-6" data-testid="iso-dashboard-charts">
      {/* Header with refresh */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{t('iso9001.dashboard_body.title')}</h2>
          <p className="text-sm text-gray-500">{t('iso9001.dashboard_body.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleBatchExport}
            disabled={isExporting}
            data-testid="batch-export-btn"
          >
            <Archive className="h-4 w-4 mr-1" />
            {isExporting ? t('iso9001.dashboard_body.exporting') : t('iso9001.dashboard_body.exportAll')}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchChartData} data-testid="btn-fetchchartdata">
            <RefreshCcw className="h-4 w-4 mr-1" />
            {t('iso9001.dashboard_body.refresh')}
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard 
          title={t('iso9001.stats.documentsPending')}
          value={documents.pending_review} 
          icon={FileText}
          color="text-blue-600"
          subtitle={t('iso9001.stats.overdue', { count: documents.overdue_review })}
          onClick={() => onNavigate?.('documents')}
        />
        <StatCard 
          title={t('iso9001.stats.openNcrs')}
          value={ncrs.open} 
          icon={AlertTriangle}
          color={ncrs.critical > 0 ? "text-red-600" : "text-yellow-600"}
          subtitle={t('iso9001.stats.critical', { count: ncrs.critical })}
          onClick={() => onNavigate?.('ncr-capa')}
        />
        <StatCard 
          title={t('iso9001.stats.openCapas')}
          value={capas.open} 
          icon={ClipboardCheck}
          color={capas.overdue > 0 ? "text-red-600" : "text-green-600"}
          subtitle={t('iso9001.stats.overdue', { count: capas.overdue })}
          onClick={() => onNavigate?.('ncr-capa')}
        />
        <StatCard 
          title={t('iso9001.stats.upcomingAudits')}
          value={audits.upcoming} 
          icon={Calendar}
          color="text-purple-600"
          subtitle={t('iso9001.stats.inProgress', { count: audits.in_progress })}
          onClick={() => onNavigate?.('audits')}
        />
        <StatCard 
          title={t('iso9001.stats.pendingTraining')}
          value={training.pending} 
          icon={GraduationCap}
          color="text-indigo-600"
          subtitle={t('iso9001.stats.certsExpiring', { count: training.expiring_certifications })}
          onClick={() => onNavigate?.('training')}
        />
      </div>

      {/* Alerts */}
      {(ncrs.critical > 0 || capas.overdue > 0 || documents.overdue_review > 0) && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-700 flex items-center gap-2 text-base">
              <AlertCircle className="h-5 w-5" />
              {t('iso9001.alerts.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {ncrs.critical > 0 && (
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  {t('iso9001.alerts.criticalNcrs', { count: ncrs.critical })}
                </div>
              )}
              {capas.overdue > 0 && (
                <div className="flex items-center gap-2 text-red-700">
                  <Clock className="h-4 w-4" />
                  {t('iso9001.alerts.overdueCapas', { count: capas.overdue })}
                </div>
              )}
              {documents.overdue_review > 0 && (
                <div className="flex items-center gap-2 text-orange-700">
                  <FileText className="h-4 w-4" />
                  {t('iso9001.alerts.overdueDocs', { count: documents.overdue_review })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="overview" className="flex items-center gap-1" data-testid="tab-overview">
            <BarChart3 className="h-4 w-4" />
            {t('iso9001.views.charts')}
          </TabsTrigger>
          <TabsTrigger value="recent" className="flex items-center gap-1" data-testid="tab-recent">
            <Clock className="h-4 w-4" />
            {t('iso9001.views.recent')}
          </TabsTrigger>
        </TabsList>

        {/* Charts View */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          {isLoadingCharts ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">{t('iso9001.dashboard_body.loadingCharts')}</div>
            </div>
          ) : chartData ? (
            <>
              {/* NCR & CAPA Charts Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <PieChartCard 
                  title={t('iso9001.charts.ncrsBySeverity')}
                  data={chartData.ncr_by_severity} 
                  colors={COLORS.severity}
                />
                <BarChartCard 
                  title={t('iso9001.charts.ncrsByStatus')}
                  data={chartData.ncr_by_status}
                  color="#eab308"
                />
                <PieChartCard 
                  title={t('iso9001.charts.capasByPriority')}
                  data={chartData.capa_by_priority} 
                  colors={COLORS.priority}
                />
                <BarChartCard 
                  title={t('iso9001.charts.capasByStatus')}
                  data={chartData.capa_by_status}
                  color="#22c55e"
                />
              </div>

              {/* Trend & Document Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <TrendChartCard 
                  title={t('iso9001.charts.ncrTrend')}
                  data={chartData.ncr_trend}
                  dataKey="ncrs"
                  color="#eab308"
                />
                <PieChartCard 
                  title={t('iso9001.charts.docsByStatus')}
                  data={chartData.doc_by_status} 
                  colors={COLORS.status}
                />
                <BarChartCard 
                  title={t('iso9001.charts.auditsByStatus')}
                  data={chartData.audit_by_status}
                  color="#8b5cf6"
                />
              </div>

              {/* Timeline Charts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TimelineChartCard 
                  title={t('iso9001.charts.docsDue')}
                  data={chartData.docs_due_timeline}
                />
                <TimelineChartCard 
                  title={t('iso9001.charts.upcomingAudits90')}
                  data={chartData.audits_timeline}
                />
                <TimelineChartCard 
                  title={t('iso9001.charts.certsExpiring')}
                  data={chartData.cert_expiry_timeline}
                />
              </div>

              {/* Conformity Scores */}
              {chartData.conformity_scores?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{t('iso9001.charts.conformityScores')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={chartData.conformity_scores}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="audit_id" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="score" fill="#22c55e" radius={[4, 4, 0, 0]} name="Score %" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {t('iso9001.dashboard_body.chartsFailed')}
            </div>
          )}
        </TabsContent>

        {/* Recent Activity View */}
        <TabsContent value="recent" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Recent NCRs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    {t('iso9001.recent.ncrs')}
                  </span>
                  <Badge variant="outline">{t('iso9001.recent.thisMonth', { count: ncrs.last_30_days })}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ncrs.recent?.length > 0 ? (
                  <div className="space-y-1">
                    {ncrs.recent.map((ncr) => (
                      <RecentItem 
                        key={ncr.id}
                        number={ncr.ncr_number}
                        title={ncr.title}
                        status={ncr.status}
                        severity={ncr.severity}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">{t('iso9001.recent.noNcrs')}</p>
                )}
              </CardContent>
            </Card>

            {/* Recent CAPAs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-green-600" />
                    {t('iso9001.recent.capas')}
                  </span>
                  <Badge variant="outline">{t('iso9001.recent.highPriority', { count: capas.high_priority })}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {capas.recent?.length > 0 ? (
                  <div className="space-y-1">
                    {capas.recent.map((capa) => (
                      <RecentItem 
                        key={capa.id}
                        number={capa.capa_number}
                        title={capa.title}
                        status={capa.status}
                        severity={capa.priority}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">{t('iso9001.recent.noCapas')}</p>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Audits */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  {t('iso9001.recent.upcomingAudits')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {audits.upcoming_list?.length > 0 ? (
                  <div className="space-y-2">
                    {audits.upcoming_list.map((audit) => (
                      <div key={audit.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium text-sm">{audit.audit_number}</p>
                          <p className="text-xs text-gray-500">{audit.title}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs">
                            {audit.audit_type}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {<DateText value={audit.planned_date} />}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">{t('auto.iso.noUpcomingAudits')}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Certification Expiration Widget */}
          <Card className="mt-6" data-testid="certification-expiry-widget">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-600" />
                  {t('auto.iso.certificationExpirations')}
                  {expiringCerts && (expiringCerts.expired > 0 || expiringCerts.expiring_soon > 0) && (
                    <Badge className={expiringCerts.expired > 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                      {expiringCerts.expired > 0 ? `${expiringCerts.expired} expired` : `${expiringCerts.expiring_soon} expiring`}
                    </Badge>
                  )}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendAllNotifications}
                    disabled={isSendingNotification || !expiringCerts?.total}
                    data-testid="notify-all-btn"
                  >
                    <Bell className="h-3 w-3 mr-1" />
                    {isSendingNotification ? t('auto.iso.sending') : t('auto.iso.notifyAll')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchExpiringCertifications}
                   data-testid="btn-fetchexpiringcertifications">
                    <RefreshCcw className="h-3 w-3" />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingCerts ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-sm text-gray-500">{t('auto.iso.loadingCertifications')}</div>
                </div>
              ) : expiringCerts?.certifications?.length > 0 ? (
                <div className="space-y-2">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-lg font-bold text-gray-700">{expiringCerts.total}</p>
                      <p className="text-xs text-gray-500">{t('auto.iso.total')}</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-red-600">{expiringCerts.expired}</p>
                      <p className="text-xs text-gray-500">{t('auto.iso.expired')}</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded">
                      <p className="text-lg font-bold text-amber-600">{expiringCerts.expiring_soon}</p>
                      <p className="text-xs text-gray-500">{t('auto.iso.expiringSoon')}</p>
                    </div>
                  </div>

                  {/* Certification List */}
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {expiringCerts.certifications.slice(0, 10).map((cert, idx) => {
                      const isExpired = cert.is_expired;
                      const isUrgent = cert.days_until_expiry <= 7 && !isExpired;
                      const isWarning = cert.days_until_expiry <= 30 && !isExpired && !isUrgent;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isExpired ? 'bg-red-50 border-red-200' : 
                            isUrgent ? 'bg-orange-50 border-orange-200' : 
                            isWarning ? 'bg-amber-50 border-amber-200' : 
                            'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isExpired ? 'bg-red-100' : isUrgent ? 'bg-orange-100' : 'bg-amber-100'
                            }`}>
                              <User className={`h-5 w-5 ${
                                isExpired ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-amber-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{cert.employee_name}</p>
                              <p className="text-xs text-gray-500">{cert.certification_name}</p>
                              <p className="text-xs text-gray-400">{cert.training_number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              isExpired ? 'bg-red-100 text-red-800' :
                              isUrgent ? 'bg-orange-100 text-orange-800' :
                              isWarning ? 'bg-amber-100 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {isExpired ? 'EXPIRED' : `${cert.days_until_expiry} days`}
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">
                              {<DateText value={cert.expiry_date} />}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {expiringCerts.certifications.length > 10 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      Showing 10 of {expiringCerts.certifications.length} certifications
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Award className="h-12 w-12 text-green-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{t('auto.iso.noCertificationsExpiringInTheNext')}</p>
                  <p className="text-xs text-green-600 mt-1">{t('auto.iso.allCertificationsAreUpToDate')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ISODashboardCharts;
